import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const popIn = keyframes`
  0%   { opacity: 0; transform: scale(0.85); }
  60%  { transform: scale(1.06); }
  100% { opacity: 1; transform: scale(1); }
`;

/* ── Circular progress widget (bottom-left anchor) ─────────────────── */
export const CircularWidget = styled.button<{ $pct: number }>`
  position: fixed;
  bottom: 24px;
  left: 24px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: conic-gradient(
    #006fbb ${({ $pct }) => $pct * 3.6}deg,
    #e4e5e7 0deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  z-index: 500;
  padding: 0;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.05);
  }

  .inner {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: var(--p-color-text);
    line-height: 1;
  }

`;

/* ── Guide panel (floats above the widget) ──────────────────────────── */
export const GuidePanel = styled.div`
  position: fixed;
  bottom: 96px;
  left: 24px;
  width: 400px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16);
  z-index: 501;
  overflow: hidden;
  animation: ${fadeIn} 0.2s ease;

  @media (max-width: 480px) {
    left: 16px;
    right: 16px;
    width: auto;
    bottom: 88px;
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;

  .collapse-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    line-height: 0;
    border-radius: 4px;
    color: var(--p-color-text-secondary);

    &:hover {
      background: #f1f2f3;
    }
  }

  .title {
    font-size: 14px;
    font-weight: 600;
    color: var(--p-color-text);
  }
`;

export const DiscountBanner = styled.div<{ $completed?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  margin: 8px 8px 0;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e4e5e7;

  .icon-wrap {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 8px;
    background: ${({ $completed }) => ($completed ? '#e3f1df' : '#fff4e5')};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .text {
    flex: 1;
    font-size: 12px;
    color: var(--p-color-text);
    line-height: 1.4;

    strong {
      font-weight: 600;
    }
  }
`;

export const ClaimGiftWrap = styled.div`
  padding: 12px 8px 4px;

  .Polaris-Button {
    border-radius: 8px;
  }
`;

export const ProgressSection = styled.div`
  padding: 10px 16px 6px;
  display: flex;
  align-items: center;
  gap: 10px;

  .label {
    font-size: 12px;
    color: var(--p-color-text-secondary);
    white-space: nowrap;
    flex-shrink: 0;
  }

  .bar-wrap {
    flex: 1;
    height: 6px;
    background: #e4e5e7;
    border-radius: 3px;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    background: #202223;
    border-radius: 3px;
    transition: width 0.4s ease;
  }
`;

export const TaskList = styled.div<{ $locked?: boolean }>`
  padding: 8px;
  pointer-events: ${({ $locked }) => ($locked ? 'none' : 'auto')};
  user-select: ${({ $locked }) => ($locked ? 'none' : 'auto')};
`;

export const TaskRow = styled.div<{ $done: boolean; $expanded?: boolean; $pending?: boolean }>`
  padding: 10px 16px;
  border-radius: 8px;
  background: ${({ $expanded }) => ($expanded ? '#f6f6f7' : '#fff')};
  cursor: pointer;
  opacity: ${({ $pending }) => ($pending ? 0.55 : 1)};
  filter: ${({ $pending }) => ($pending ? 'blur(0.4px)' : 'none')};
  pointer-events: ${({ $pending }) => ($pending ? 'none' : 'auto')};
  user-select: ${({ $pending }) => ($pending ? 'none' : 'auto')};
  transition: opacity 0.15s ease, filter 0.15s ease;

  .row-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .row-right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .avatar-stack {
    display: inline-flex;
    & > div {
      margin-left: -8px;
      border: 2px solid #fff;
    }
    & > div:first-child {
      margin-left: 0;
    }
  }

  .check {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border-radius: 50%;
    border: ${({ $done }) => ($done ? 'none' : '2px dashed #adb0b3')};
    background: ${({ $done }) => ($done ? '#202223' : 'transparent')};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: default;
  }

  .task-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--p-color-text);
  }

  .task-body {
    margin-top: 8px;
    margin-left: 30px;

    .desc {
      font-size: 12px;
      color: var(--p-color-text-secondary);
      line-height: 1.5;
      margin-bottom: 8px;
    }
  }
`;

/* ── Review modal overlay ───────────────────────────────────────────── */
export const ReviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ReviewCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 28px;
  width: 420px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  animation: ${fadeIn} 0.2s ease;

  @media (max-width: 480px) {
    width: calc(100vw - 32px);
    padding: 24px 16px 20px;
  }

  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--p-color-text-secondary);
    padding: 4px;
    line-height: 0;
    border-radius: 4px;

    &:hover { background: #f1f2f3; }
  }

  .review-icon {
    margin: 0 auto 16px;
    width: 80px;
    height: 80px;
  }

  .review-title {
    font-size: 18px;
    font-weight: 700;
    color: var(--p-color-text);
    margin-bottom: 8px;
  }

  .review-body {
    font-size: 13px;
    color: var(--p-color-text-secondary);
    line-height: 1.5;
    margin-bottom: 20px;
  }

  .stars {
    display: flex;
    justify-content: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .star-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    line-height: 0;
    transition: transform 0.15s ease;

    &:hover { transform: scale(1.08); }
  }
`;

/* ── Discount transition (after star click) ─────────────────────────── */
export const DiscountTransition = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 12px 0 8px;
  animation: ${fadeIn} 0.25s ease;

  .spinner {
    width: 44px;
    height: 44px;
    border: 3px solid #e4e5e7;
    border-top-color: #006fbb;
    border-radius: 50%;
    animation: ${spin} 0.75s linear infinite;
  }

  .transition-title {
    font-size: 17px;
    font-weight: 700;
    color: var(--p-color-text);
    animation: ${fadeIn} 0.3s ease 0.1s both;
  }

  .transition-body {
    font-size: 13px;
    color: var(--p-color-text-secondary);
    line-height: 1.5;
    animation: ${fadeIn} 0.3s ease 0.2s both;
  }

  .discount-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #e3f1df;
    color: #1a7f4b;
    font-size: 13px;
    font-weight: 600;
    padding: 7px 16px;
    border-radius: 20px;
    border: 1px solid #b5dfca;
    animation: ${popIn} 0.35s ease 0.3s both;
  }
`;

/* ── Mobile launcher (full-width pinned bottom) ─────────────────────── */
export const LauncherBar = styled.button<{ $pct: number; $completed?: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 60px;
  border: none;
  border-top: 1px solid #e4e5e7;
  background: #fff;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  /* BFS412 Bug 1: phải nằm DƯỚI lớp overlay Polaris (PositionedOverlay = 400:
     popover/datepicker/tooltip) để Apply/Cancel của popover mở ghim đáy không bị
     thanh launcher che, nhưng vẫn TRÊN page content (z auto = 0). Modal (~519) vẫn
     phủ launcher khi cần. */
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  cursor: pointer;

  .ring {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 50%;
    background: ${({ $completed, $pct }) =>
      $completed ? '#e3f1df' : `conic-gradient(#006fbb ${$pct * 3.6}deg, #e4e5e7 0deg)`};
    display: flex;
    align-items: center;
    justify-content: center;

    .ring-inner {
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 700;
      color: var(--p-color-text);
    }
  }

  .launcher-text {
    flex: 1;
    text-align: left;
    line-height: 1.25;

    .title {
      font-size: 14px;
      font-weight: 600;
      color: var(--p-color-text);
    }
    .subtitle {
      font-size: 12px;
      color: var(--p-color-text-secondary);
    }
  }

  .chevron {
    flex-shrink: 0;
    color: var(--p-color-text-secondary);
  }
`;

/* ── Mobile bottom-sheet ────────────────────────────────────────────── */
export const BottomSheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: 600;
  display: flex;
  align-items: flex-end;
`;

export const SheetMask = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
`;

export const SheetPanel = styled.div`
  position: relative;
  width: 100%;
  max-height: 86vh;
  background: #fff;
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: #d2d5d8;
  }
`;

export const SheetHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px 10px;

  .title {
    font-size: 15px;
    font-weight: 600;
    color: var(--p-color-text);
  }
`;

export const SheetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 16px;
`;
